import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, firstValueFrom } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { APP_VERSION } from 'src/app-version';
import { ApiService } from 'src/app/services/api.service';
import { ServerTypes } from 'src/app/models/servers';
import { appAuthCleanUpItems } from 'src/app/models/storage-logout-remove-items';
import { StorageService } from 'src/app/services/storage.service';
import { CoreStorageEnum } from 'src/app/models/core-storage.enum';

@Injectable()
export class AuthService {
  private $isLogged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    this.isAuthenticated()
  );
  public isLogged: Observable<boolean>;
  public tempLogged = false;
  public mfaSession: string;
  public mfaProvider: string;
  public mfaEnrollmentRequired = false;

  constructor(
    private httpClient: HttpClient,
    private apiService: ApiService,
    private storage: StorageService,
  ) {
    this.isLogged = this.$isLogged.asObservable();
  }

  public get url(): string {
    return this.apiService.getCurentServer(ServerTypes.auth);
  }

  public isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  public getAuthToken(): string {
    return this.storage.getLocalStorageItem(CoreStorageEnum.tokenName);
  }

  public setAuthToken(token: string, type: string, clientId: string) {
    this.storage.setLocalStorageItem(CoreStorageEnum.tokenName, token);
    this.storage.setLocalStorageItem(CoreStorageEnum.tokenType, type);
    this.storage.setLocalStorageItem(CoreStorageEnum.clientId, clientId);
  }

  public getClientId(): string {
    return this.storage.getLocalStorageItem(CoreStorageEnum.clientId);
  }

  public getLastCheck(): Date {
    return new Date(
      this.storage.getLocalStorageItem(CoreStorageEnum.tokenLastCheck)
    );
  }
  public setLastCheck() {
    this.storage.setLocalStorageItem(
      CoreStorageEnum.tokenLastCheck,
      new Date().toString()
    );
  }

  public getTokenType(): string {
    return this.storage.getLocalStorageItem(CoreStorageEnum.tokenType);
  }

  public requestIp(
    username: string,
    password: string,
    ip: string,
    location: string,
    periodDays: number,
    clientId: string
  ): Observable<any> {
    const body = {
      version: APP_VERSION,
      ip,
      location,
      periodDays,
      clientId,
      username,
      password,
    };

    return this.httpClient
      .post(`${this.url}/v1/auth/iprequest`, body, {
        headers: this.apiService.getHeaders(ServerTypes.auth, 0),
      })
      .pipe(tap((res) => {}));
  }

  public validateTwoFactorCode(tokenCode: string, trustDevice: boolean): Observable<any> {
    const body = { mfaSession: this.mfaSession, tokenCode, trustDevice };
    return this.httpClient
      .post(`${this.url}/v1/auth/mfa`, body, {
        headers: this.apiService.getHeaders(ServerTypes.auth, 0),
        withCredentials: true,
      })
      .pipe(
        tap((res) => {
          this.storeSession(res);
          this.mfaSession = undefined;
          this.mfaProvider = undefined;
        })
      );
  }

  // Forced enrollment during login: the application requires MFA but the user has no
  // authenticator yet. Both calls are keyed on the ENROLL session from login, no bearer token.
  public beginMfaEnrollment(): Observable<any> {
    return this.httpClient.post(
      `${this.url}/v1/auth/mfa/enroll/session`,
      { mfaSession: this.mfaSession },
      { headers: this.apiService.getHeaders(ServerTypes.auth, 0) }
    );
  }

  public confirmMfaEnrollment(enrollment: any, tokenCode: string, trustDevice: boolean): Observable<any> {
    const body = {
      mfaSession: this.mfaSession,
      secret: enrollment.secret,
      enrollmentToken: enrollment.enrollmentToken,
      tokenCode,
      trustDevice,
    };
    return this.httpClient
      .post(`${this.url}/v1/auth/mfa/enroll/session/confirm`, body, {
        headers: this.apiService.getHeaders(ServerTypes.auth, 0),
        withCredentials: true,
      })
      .pipe(
        tap((res) => {
          this.storeSession(res);
          this.mfaSession = undefined;
          this.mfaProvider = undefined;
          this.mfaEnrollmentRequired = false;
        })
      );
  }

  public checkTrustedDevice(clientId: string): Observable<boolean> {
    return this.httpClient
      .get<{ trusted: boolean }>(
        `${this.url}/v1/auth/trusted-device?clientId=${encodeURIComponent(clientId)}`,
        { headers: this.apiService.getHeaders(ServerTypes.auth, 0), withCredentials: true }
      )
      .pipe(map((res) => res.trusted));
  }

  public revokeTrustedDevice(clientId: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.url}/v1/auth/trusted-device?clientId=${encodeURIComponent(clientId)}`,
      { headers: this.apiService.getHeaders(ServerTypes.auth, 0), withCredentials: true }
    );
  }

  public login(
    username: string,
    password: string,
    clientId: string
  ): Observable<any> {
    const body = {
      version: APP_VERSION,
      clientId,
      username,
      password,
    };
    this.storage.removeLocalStorageItem(CoreStorageEnum.tokenName);
    this.storage.removeLocalStorageItem(CoreStorageEnum.tokenType);

    return this.httpClient
      .post(`${this.url}/v1/auth/token`, body, {
        headers: this.apiService.getHeaders(ServerTypes.auth, 0),
        withCredentials: true,
      })
      .pipe(
        tap((res) => {
          this.storage.setSessionStorageItem(
            CoreStorageEnum.username,
            username
          );

          if (res.mfaRequired) {
            this.mfaSession = res.mfaSession;
            this.mfaProvider = res.provider;
            this.mfaEnrollmentRequired = !!res.mfaEnrollmentRequired;
            return;
          }

          this.storeSession(res);
        })
      );
  }

  private storeSession(res: any) {
    this.storage.setLocalStorageItem(ServerTypes.boapi, res.url);
    res.servers.forEach(server => {
      this.storage.setLocalStorageItem(server.name, server.url);
    })
    this.setAuthToken(
      res[CoreStorageEnum.tokenName],
      res[CoreStorageEnum.tokenType],
      res.clientId
    );
    this.setLastCheck();
    this.$isLogged.next(true);
  }

  public logout(): Observable<any> {
    if (this.isAuthenticated()) {
      const headers = this.apiService
        .getHeaders(ServerTypes.auth, 0)
        .set('Authorization', this.getTokenType() + ` ${this.getAuthToken()}`);
      return this.httpClient
        .delete(`${this.url}/v1/auth/token`, { headers })
        .pipe(
          finalize(() => {
            this.clearUserData();
          })
        );
    } else {
      this.$isLogged.next(false);
      return of(false);
    }
  }

  // The guard calls this on every route change; the token is only verified
  // against the auth server once this long after the previous check.
  private static readonly TOKEN_CHECK_INTERVAL_MS = 30 * 60 * 1000;
  // Nested routes run the guard at each level concurrently. Sharing the
  // in-flight check means one navigation makes one request, not one per level.
  private tokenCheck: Promise<boolean> | null = null;

  public async isAuthenticatedAsync() {
    if (!this.isAuthenticated()) {
      return false;
    }

    if (this.getDateDiff(this.getLastCheck()) < AuthService.TOKEN_CHECK_INTERVAL_MS) {
      return true;
    }

    if (!this.tokenCheck) {
      const headers = this.apiService
        .getHeaders(ServerTypes.auth, 0)
        .set('Authorization', this.getTokenType() + ` ${this.getAuthToken()}`);
      this.tokenCheck = firstValueFrom(this.httpClient
        .get(`${this.url}/v1/auth/token`, { headers }), { defaultValue: undefined })
        .then(
          () => {
            this.tempLogged = true;
            this.setLastCheck();
            return true;
          },
          () => {
            this.tempLogged = false;
            this.clearUserData();
            return false;
          }
        )
        .finally(() => {
          this.tokenCheck = null;
        });
    }
    return this.tokenCheck;
  }
  public getDateDiff(startDate) {
    if (startDate == null) {
      return 99999999;
    }
    return new Date().getTime() - startDate.getTime();
  }

  public clearUserData() {
    this.storage.removeLocalStorageItem(CoreStorageEnum.tokenName);
    this.storage.removeLocalStorageItem(CoreStorageEnum.tokenLastCheck);
    this.storage.removeLocalStorageItem(CoreStorageEnum.tokenType);

    if (appAuthCleanUpItems.length) {
      appAuthCleanUpItems.forEach((item) => {
        this.storage.removeSessionStorageItem(String(item));
      });
    }
    this.$isLogged.next(false);
  }
}
